import React, { useState, useEffect } from "react";
import Input from "../../../../core/Inputs/DefaultInput";
import Button from "../../../../core/Button";
import Typography from "../../../../core/Typography";
import Svg from "../../../../component/common/Svg";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { apiRequest } from "../../../../services/apiService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MfaVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const email = searchParams.get("email");
  const userId = searchParams.get("user_id") || location.state?.user_id;

  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (!email || !userId) {
      window.location = "/";
    }
  }, []);

  useEffect(() => {
    if (timer <= 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const formatTimer = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m < 10 ? "0" + m : m}:${s < 10 ? "0" + s : s}`;
  };

  const handleVerify = async () => {
    if (!otp.trim()) {
      setOtpError("Please enter the OTP");
      return;
    }
    setOtpError("");
    setLoading(true);

    try {
      const { response, error } = await apiRequest({
        method: "POST",
        url: "/users/verify-mfa",
        data: { user_id: userId, otp: otp.trim() },
        showErrorToast: false,
      });

      if (response?.data?.statusCode === 200 && response.data.data?.token_id) {
        localStorage.setItem("token_id", response.data.data.token_id);
        toast.success("Verification successful");
        navigate("/admin/survey/list");
      } else {
        toast.error(error?.response?.data?.msg || "Invalid OTP");
      }
    } catch (e) {
      toast.error("Invalid or expired OTP");
    }
    setLoading(false);
  };

  const handleResend = async () => {
    setCanResend(false);
    setTimer(60);

    try {
      const { response } = await apiRequest({
        method: "POST",
        url: "/users/resend-mfa",
        data: { user_id: userId },
        showErrorToast: false,
      });
      if (response?.data?.statusCode === 200) {
        toast.success("A new code has been sent to your email");
      }
    } catch (e) {
      toast.error("Unable to resend code. Please try again.");
    }
  };

  return (
    <div className="d-flex flex-column site-main-wrapper login-page">
      <div className="flex-grow-1 main-content-wrap d-flex align-items-center justify-content-center">
        <div className="container">
          <div className="form-outer-wrapper">
            <div className="logo-wrap text-center">
              <Link to="/" className="home-logo">
                <Svg name="logo" />
              </Link>
            </div>
            <div className="form-wrapper">
              <div className="form-heading text-center">
                <h3>Verify your identity</h3>
                <p>
                  We've sent a 6-digit verification code to{" "}
                  <span className="black">{email}</span>
                </p>
              </div>
              <div className="field-wrapper">
                {otpError && (
                  <div className="validation-msg" style={{ top: "57%" }}>
                    {otpError}
                  </div>
                )}
                <Input
                  type="text"
                  view="outlined"
                  dense={false}
                  color="default"
                  placeholder="Enter 6-digit code"
                  clearButton={true}
                  fill={true}
                  className="formsinput"
                  value={otp}
                  name="otp"
                  onInput={(e) => {
                    setOtp(e.target.value);
                    setOtpError("");
                  }}
                  inputMode="numeric"
                  maxLength={6}
                  onKeyPress={(e) => {
                    if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                  }}
                />
                <div className="additional-info d-flex justify-content-between mb__20 pt-1">
                  <div className="text-primary">
                    <Typography type="caption" fill color="primary" colorStep={60} tagName="div">
                      {!canResend && <b>{formatTimer(timer)}</b>}
                    </Typography>
                  </div>
                  <a>
                    <Typography type="caption" fill colorStep={60} tagName="div">
                      <b
                        onClick={canResend ? handleResend : undefined}
                        className={canResend ? "text-primary" : "text-secondary"}
                        style={{ cursor: canResend ? "pointer" : "default" }}
                      >
                        Resend code
                      </b>
                    </Typography>
                  </a>
                </div>
              </div>
              <div className="button-wrap">
                <button
                  type="button"
                  className="formsbutton btn-primary btn mb__20 w-100"
                  onClick={handleVerify}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="white-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      <span className="visually-hidden">Loading...</span>
                    </>
                  ) : (
                    "Verify"
                  )}
                </button>
              </div>
              <div className="form-text text-center">
                <p>
                  Wrong email address? <Link to="/">Go back</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaVerification;
